// Frontend helper for interacting with PinkLock v2.
//
// Flow used by the invest page:
//   1) Ensure GRX allowance to PinkLock covers `amountGRX`  (approve if not)
//   2) Call pinkLock.lock(owner, grxToken, false, amountGRX, unlockDate, "GRX-<plan>")
//   3) Parse the LockAdded event from the receipt → lockId
//   4) WAIT for the lock to be visible on the RPC nodes that will run
//      eth_estimateGas / eth_call for the next tx (invest). Otherwise the
//      invest reverts because pinkSaleLocker.locks(id) returns empty bytes
//      on a node that hasn't yet caught up with the lock tx.
//   5) Pass lockId to plan-logic invest()

// Extra time added to unlockDate beyond the strict plan duration.
// Contract requires tgeDate >= block.timestamp(invest) + planLockDuration.
// 24h buffer means the invest tx can mine any time within the next day.
const LOCK_BUFFER_SECONDS = 24 * 60 * 60;

const ZERO_ADDR = '0x0000000000000000000000000000000000000000';

export const planLockSeconds = (planType) => {
  const day = 24 * 60 * 60;
  if (planType === 1) return 3 * 365 * day;
  if (planType === 2) return 5 * 365 * day;
  if (planType === 3) return 7 * 365 * day;
  return 0;
};

// Deep-link to PinkSale's lock detail page for a given lockId.
// PinkLock v2 record page: https://www.pinksale.finance/pinklock2/record/{id}?chain=BSC[-Testnet]
export const pinksaleLockUrl = (lockId, network) => {
  const chain = network === 'testnet' ? 'BSC-Testnet' : 'BSC';
  return `https://www.pinksale.finance/pinklock2/record/${String(lockId)}?chain=${chain}`;
};

// Best-guess plan type from a PinkSale lock's unlock horizon.
// Returns 1 / 2 / 3, or null if the lock doesn't look like one of our plans.
// Tolerance ±60 days.
export const inferPlanFromUnlock = (unlockDateSec, lockDateSec) => {
  const day = 24 * 60 * 60;
  const dur = Number(unlockDateSec) - Number(lockDateSec || Math.floor(Date.now() / 1000));
  if (dur <= 0) return null;
  const years = dur / (365 * day);
  const tol = 60 / 365;
  if (Math.abs(years - 3) < tol) return 1;
  if (Math.abs(years - 5) < tol) return 2;
  if (Math.abs(years - 7) < tol) return 3;
  return null;
};

export const computeUnlockDate = (planType) => {
  const now = Math.floor(Date.now() / 1000);
  return now + planLockSeconds(planType) + LOCK_BUFFER_SECONDS;
};

export const parseLockIdFromReceipt = (pinkLockContract, receipt) => {
  const iface = pinkLockContract.interface;
  for (const log of receipt.logs || []) {
    if (log.address && String(log.address).toLowerCase() !== String(pinkLockContract.target).toLowerCase()) continue;
    try {
      const parsed = iface.parseLog({ topics: log.topics, data: log.data });
      if (parsed && parsed.name === 'LockAdded') {
        const id = parsed.args?.id ?? parsed.args?.[0];
        if (id != null) return BigInt(id);
      }
    } catch { /* not a matching event */ }
  }
  return null;
};

/**
 * Poll one or more PinkLock reader contracts until they report a non-zero
 * `token` for the given lockId — proof that the RPC node the reader talks
 * to has caught up and can serve the lock in eth_call / eth_estimateGas.
 *
 * Returns true if ALL readers see the lock within `timeoutMs`.
 */
export const waitForLockVisible = async (readers, lockId, opts = {}) => {
  const readerList = Array.isArray(readers) ? readers : [readers];
  const timeoutMs  = opts.timeoutMs  ?? 30000;
  const intervalMs = opts.intervalMs ?? 1000;
  const onTick     = opts.onTick;
  const start = Date.now();

  while (Date.now() - start < timeoutMs) {
    let allSeen = true;
    for (const r of readerList) {
      try {
        const lock = await r.getLockById(lockId);
        const token = lock?.token ?? lock?.[1];
        if (!token || String(token).toLowerCase() === ZERO_ADDR) {
          allSeen = false;
          break;
        }
      } catch {
        allSeen = false;
        break;
      }
    }
    if (allSeen) return true;
    if (onTick) onTick(Math.floor((Date.now() - start) / 1000));
    await new Promise((res) => setTimeout(res, intervalMs));
  }
  return false;
};

/**
 * Create a PinkSale lock and return the assigned lockId, waiting for the
 * lock to be visible on the given reader(s) so the subsequent invest() call
 * doesn't race the RPC.
 *
 * @param pinkLockContract  signer-bound PinkLock (used to send lock() and later read)
 * @param pinkLockReader    optional provider-bound PinkLock (poll this too — the app
 *                          uses a separate JsonRpcProvider for reads)
 * @param onProgress        optional (label:string) => void — for UI updates
 */
export const createLockAndGetId = async ({
  pinkLockContract,
  pinkLockReader,
  grxAddress,
  ownerAddress,
  amount,
  planType,
  description = 'GRX plan lock',
  onProgress,
}) => {
  const unlockDate = computeUnlockDate(planType);

  if (onProgress) onProgress('Locking GRX on PinkSale');
  const tx = await pinkLockContract.lock(
    ownerAddress,
    grxAddress,
    false,
    amount,
    BigInt(unlockDate),
    description
  );

  // Wait for 2 confirmations — gives the network an extra block to propagate.
  const receipt = await tx.wait(2);

  const lockId = parseLockIdFromReceipt(pinkLockContract, receipt);
  if (lockId == null) {
    throw new Error('PinkSale lock created, but lockId could not be parsed from the receipt.');
  }

  // Poll BOTH the signer's PinkLock instance (whose provider will run the
  // upcoming invest() estimateGas) and the app's read-provider PinkLock,
  // so we're 100% sure the lock is queryable before we call invest.
  const readers = [pinkLockContract];
  if (pinkLockReader) readers.push(pinkLockReader);

  if (onProgress) onProgress('Waiting for lock to propagate');
  const visible = await waitForLockVisible(readers, lockId, {
    timeoutMs: 45000,
    intervalMs: 1000,
    onTick: (s) => { if (onProgress) onProgress(`Waiting for lock to propagate (${s}s)`); },
  });

  if (!visible) {
    // The lock IS on-chain — the receipt proves it. RPC just isn't showing
    // it yet. Tell the user the lockId so they can retry manually if needed.
    throw new Error(
      `Lock #${lockId} was created on PinkSale but is not yet visible on the RPC after 45s. ` +
      `Wait a moment and try again — the lock already exists so no new tokens will be locked.`
    );
  }

  return { lockId, receipt, unlockDate };
};
